import React, { useRef,useCallback, useMemo  } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { useLanguage } from '../../../../contexts/LanguageContext';

import PreApprovalTable from './components/PreApprovalTable';
import PreApprovalForm from './components/PreApprovalForm';
import PreApprovalFilters from './components/PreApprovalFilters';
import HistoryDialog from './components/HistoryDialog';
import FinalyDescriptionDialog from './components/FinalyDescriptionDialog';
import CustomerDeclarationDialog from '../../customer-declaration/components/dialogs/declaration-form/CustomerDeclarationDialog';


import usePreApprovalData from './hooks/usePreApprovalData';
import usePreApprovalForm from './hooks/usePreApprovalForm';
import NewButton from '../../../../components/NewButton/NewButton';

import { preparePreApprovalForSubmission } from './utils/preApprovalTransformers';
import { usePermissions } from '../../../../hooks/usePermissions';
import  useDeclarationForm  from '../../customer-declaration/hooks/useDeclarationForm';
import { useLazyGetDeclarationByIdQuery } from '../../../../api/customerDeclarationApi';
const PreApprovalPage = () => {
    const { th } = useLanguage();

    const {
        preApprovals,
        statusCounts,
        loadedStatus,
        loading,
        filters,
        activeIndex,
        error,
        dialogVisible,
        historyDialogVisible,
        finalyDescriptionDialogVisible,
        editMode,
        currentPreApproval,
        currentHistoryRecord,
        submitted,
        filterFields,
        customFilterTypes,
        customFilterOptions,
        selectedCountries,
        selectedDealers,
        handleCountriesChange,
        handleDealersChange,
        handleDelete,
        savePreApproval,
        changePreApprovalStatus,
        handleSearch,
        handleResetFilters,
        handleApplyFilters,
        handleAddNew,
        handleEdit,
        hideDialog,
        hideFinalyDescriptionDialog,
        hideHistoryDialog,
        showHistoryDialog,
        setActiveIndex,
        setSubmitted,
        isChangingStatus,
        setFinalyDescriptionDialogVisible
    } = usePreApprovalData();

       const {
        formData: declarationFormData,
        setFormData: setDeclarationFormData,
        dialogVisible: declarationDialogVisible,  // ← rename
        editMode: declarationEditMode,            // ← rename
        handleEdit: handleEditDeclaration,
        handleClose: handleCloseDeclaration,
        errors: declarationErrors,
        handleSave: handleSaveDeclaration,
        handleDelete: handleDeleteDeclaration,
        chassisData: declarationChassisData,
        setChassisData: setDeclarationChassisData,
        updatedFiles: declarationFiles,
        onFilesChange: onDeclarationFilesChange,
        onRemoveFile: onRemoveDeclarationFile,
    } = useDeclarationForm();

    const tableFilters = useMemo(() => {
    const { searchText, ...rest } = filters;
    return rest;
}, [filters]);
    
    const [triggerGetStatementById] = useLazyGetDeclarationByIdQuery();

    const handleOpenCustomerStatement = useCallback(
        async (customerStatementNo) => {
            try {
                const result = await triggerGetStatementById(customerStatementNo).unwrap();
                if (result) {
                    handleEditDeclaration(result); // useDeclarationForm'daki handleEdit
                }
            } catch (error) {
                console.error('Customer statement yüklenemedi:', error);
            }
        },
        [triggerGetStatementById, handleEditDeclaration]
    );

    const { canCreateCustomerDeclaration } = usePermissions();

    const formProps = usePreApprovalForm(savePreApproval);

    const toast = useRef(null);
    const pendingEditRef = useRef(null);

    React.useEffect(() => {
        if (dialogVisible && editMode && pendingEditRef.current) {
            formProps.setChangeData(pendingEditRef.current);
            pendingEditRef.current = null;
        }
    }, [dialogVisible, editMode]);

    React.useEffect(() => {
        if (error) {
            toast.current.show({
                severity: 'error',
                summary: th('preApproval.messages.errorSummary'),
                detail: error.errorMessage || error.message || th('preApproval.messages.errorDetail'),
                life: 3000
            });
        }
    }, [error, th]);

    const handleSubmit = async (files = []) => {
        const success = await formProps.handleSubmit(activeIndex, editMode, currentPreApproval, hideDialog, setFinalyDescriptionDialogVisible, files);
        return success;
    };

    const handleSendForApproval = async (files = []) => {
        if (editMode && currentPreApproval && currentPreApproval.id) {
            const formSubmitResult = await savePreApproval(preparePreApprovalForSubmission(formProps.formData, true, currentPreApproval), true, currentPreApproval, true);

            if (formSubmitResult.success) {
                const statusUpdateSuccess = await changePreApprovalStatus(currentPreApproval.id, 20, '');
                if (statusUpdateSuccess) hideDialog();
            }
        } else {
            setSubmitted(true);
            if (!formProps.formData.chassisNo || !formProps.formData.failureDate) return;

            try {
                const apiData = preparePreApprovalForSubmission(formProps.formData, false, null);
                const result = await savePreApproval(apiData, false, null, true, files);

                if (result.success && result.id) {
                    const statusUpdateSuccess = await changePreApprovalStatus(result.id, 20, '');
                    if (statusUpdateSuccess) {
                        hideDialog();
                        formProps.resetForm();
                    } else {
                        hideDialog();
                        formProps.resetForm();
                        toast.current?.show({
                            severity: 'warn',
                            summary: th('common.warning'),
                            detail: th('preApproval.messages.statusUpdateFailedDraft'),
                            life: 5000
                        });
                    }
                }
            } catch (err) {
                console.error('Send for approval error:', err);
            }
        }
    };

    const handleApprove = async () => {
        if (currentPreApproval && currentPreApproval.id) {
            const success = await changePreApprovalStatus(currentPreApproval.id, 30, '');
            if (success) hideDialog();
        }
    };

    const handleReject = async (reason) => {
        if (currentPreApproval && currentPreApproval.id) {
            const success = await changePreApprovalStatus(currentPreApproval.id, 40, reason || '');
            if (success) hideDialog();
        }
    };

    const handleReturn = async (reason) => {
        if (currentPreApproval && currentPreApproval.id) {
            const success = await changePreApprovalStatus(currentPreApproval.id, 15, reason || '');
            if (success) hideDialog();
        }
    };

    const handleEditWithFormData = (preApproval) => {
        formProps.handleChange('status', preApproval.status);
        pendingEditRef.current = preApproval;
        handleEdit(preApproval);
    };

    const handleAddNewWithReset = () => {
        formProps.resetForm();
        handleAddNew();
    };

    const tabLabels = [
        { label: th('preApproval.tabs.drafts'), icon: 'pi pi-file-edit', color: 'orange', status: 10 },
        { label: th('preApproval.tabs.waitingApproval'), icon: 'pi pi-clock', color: 'blue', status: 20 },
        { label: th('preApproval.tabs.returned'), icon: 'pi pi-replay', color: 'red', status: 15 },
        { label: th('preApproval.tabs.accepted'), icon: 'pi pi-check-circle', color: 'green', status: 30 },
        { label: th('preApproval.tabs.rejected'), icon: 'pi pi-times-circle', color: 'red', status: 40 }
    ];

    return (
        <div className="card">
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="m-0">{th('preApproval.title')}</h1>
                </div>
                {canCreateCustomerDeclaration() && <NewButton onClick={handleAddNewWithReset} />}
            </div>

            <PreApprovalFilters
                filterFields={filterFields}
                customFilterOptions={customFilterOptions}
                customFilterTypes={customFilterTypes}
                handleSearch={handleSearch}
                handleApplyFilters={handleApplyFilters}
                handleResetFilters={handleResetFilters}
                selectedCountries={selectedCountries}
                onCountriesChange={handleCountriesChange}
                selectedDealers={selectedDealers}
                onDealersChange={handleDealersChange}
            />

            <PreApprovalForm
                dialogVisible={dialogVisible}
                editMode={editMode}
                submitted={submitted}
                hideDialog={hideDialog}
                formData={formProps.formData}
                handleChange={formProps.handleChange}
                activeIndex={activeIndex}
                handleSubmit={handleSubmit}
                handleSendForApproval={handleSendForApproval}
                handleApprove={handleApprove}
                handleReject={handleReject}
                handleReturn={handleReturn}
                currentPreApproval={currentPreApproval}
                setSubmitted={setSubmitted}
                isStatusChanging={isChangingStatus}               
            />

            <FinalyDescriptionDialog hideDialog={hideFinalyDescriptionDialog} dialogVisible={finalyDescriptionDialogVisible} />

            <HistoryDialog hideDialog={hideHistoryDialog} dialogVisible={historyDialogVisible} preApproval={currentHistoryRecord} />
             <CustomerDeclarationDialog
                visible={declarationDialogVisible}
                onHide={handleCloseDeclaration}
                formData={declarationFormData}
                setFormData={setDeclarationFormData}
                errors={declarationErrors}
                handleSave={handleSaveDeclaration}
                editMode={declarationEditMode}
                handleDelete={handleDeleteDeclaration}
                chassisData={declarationChassisData}
                setChassisData={setDeclarationChassisData}
                updatedFiles={declarationFiles}
                onFilesChange={onDeclarationFilesChange}
                onRemoveFile={onRemoveDeclarationFile}
            />

            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} className="preapproval-tabs">
                {tabLabels.map((tab, index) => (
                    <TabPanel
                        key={index}
                        header={
                            <span className="tab-header">
                                <i className={`${tab.icon} mr-2`}></i>
                                {tab.label}
                                <span className={`ml-2 px-2 py-1 border-round text-xs font-semibold bg-${tab.color}-100 text-${tab.color}-800`}>
                                    {activeIndex === index && loadedStatus === tab.status && !loading ? preApprovals.length : statusCounts?.[tab.status] ?? 0}
                                </span>
                            </span>
                        }
                        className="tab-content"
                    >
                        <PreApprovalTable
                            preApprovals={preApprovals}
                            loading={loading}
                            filters={tableFilters}
                            handleEdit={handleEditWithFormData}
                            handleDelete={handleDelete}
                            showHistoryDialog={showHistoryDialog}
                            tabLabel={tab.label}
                            onOpenCustomerStatement={handleOpenCustomerStatement}
                        />
                       
                    </TabPanel>
                ))}
            </TabView>
        </div>
    );
};

export default PreApprovalPage;
